import { FocusMonitor } from '@angular/cdk/a11y';
import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  ViewChild,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PhoneInputComponent } from '@vendasta/galaxy/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, firstValueFrom } from 'rxjs';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  COMPANY_NAME_TOKEN,
  ConversationService,
  InboxContact,
  InboxService,
  PARTNER_ID_TOKEN,
  ParticipantService,
} from '../../../../core/src/index';

@Component({
  selector: 'inbox-create-contact-form',
  templateUrl: './create-contact-form.component.html',
  styleUrls: ['./create-contact-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateContactFormComponent implements AfterViewInit {
  @ViewChild('phoneInput') phoneInput: PhoneInputComponent;
  @ViewChild('firstNameInput') firstNameInput: ElementRef;

  readonly MIN_CHARACTERS = 2;

  readonly contactForm = this.formBuilder.group({
    firstName: ['', [Validators.required, Validators.minLength(this.MIN_CHARACTERS)]],
    lastName: [''],
    phone: [Number(null), [Validators.minLength(6)]],
    email: ['', [Validators.email, Validators.minLength(6)]],
  });

  get errors() {
    return {
      firstName: this.contactForm.controls.firstName.errors,
      lastName: this.contactForm.controls.lastName.errors,
      email: this.contactForm.controls.email.errors,
      phone: this.contactForm.controls.phone.errors,
    };
  }

  isLoading = false;
  isSubmitted = false;

  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));

  constructor(
    private readonly focus: FocusMonitor,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    private cdRef: ChangeDetectorRef,
    @Inject(COMPANY_NAME_TOKEN) readonly companyName$: Observable<string>,
    private readonly formBuilder: FormBuilder,
    private readonly conversationService: ConversationService,
    private readonly inboxService: InboxService,
    private readonly participantService: ParticipantService,
    private readonly snackbarService: SnackbarService,
    private readonly analyticsService: ProductAnalyticsService,
  ) {
    const currentNavigation = this.router.getCurrentNavigation();
    if (currentNavigation) {
      this.contactForm.patchValue(currentNavigation.extras.state);
    }
  }

  ngAfterViewInit(): void {
    this.focus.focusVia(this.firstNameInput, 'program');
    this.cdRef.detectChanges();
  }

  async submit(): Promise<void> {
    this.phoneInput.rawInputControl.updateValueAndValidity();
    this.isSubmitted = true;

    if (!(this.contactForm.get('phone').value || this.contactForm.get('email').value)) {
      this.snackbarService.openErrorSnack('INBOX.ERROR.PHONE_OR_EMAIL_REQUIRED');
      return;
    }

    if (!this.contactForm.valid) {
      this.snackbarService.openErrorSnack('INBOX.ERROR.CORRECT_INVALID_REQUIRED_FIELDS');
      return;
    }

    this.startLoading();

    try {
      const formValue = this.contactForm.value;
      const newContact = await firstValueFrom(
        this.participantService.addContact({
          firstName: formValue.firstName,
          lastName: formValue.lastName,
          phone: `${formValue.phone}`,
          email: formValue.email,
          permissionToContact: true,
          accountGroupId: this.accountGroupId(),
          partnerId: this.partnerId(),
        }),
      );

      if (!newContact?.contactId) throw new Error('Contact not created');

      this.analyticsService.trackEvent('inbox', 'new-message', 'add-contact-success', 0, {
        accountGroupId: this.accountGroupId(),
        partnerId: this.partnerId(),
        location: this.inboxService.platformLocation,
        channel: this.conversationService.getConversationChannel(),
      });

      await this.returnToNewMessage(newContact);
    } catch (error) {
      console.warn('submit', error.message);
      if (error.status === 409) {
        this.snackbarService.openErrorSnack('INBOX.ERROR.CONTACT_EXISTS');
      } else {
        this.snackbarService.openErrorSnack('INBOX.ERROR.CREATE_CONTACT');
      }

      this.analyticsService.trackEvent('inbox', 'new-message', 'add-contact-error', 0, {
        accountGroupId: this.accountGroupId(),
        partnerId: this.partnerId(),
        location: this.inboxService.platformLocation,
        channel: this.conversationService.getConversationChannel(),
        message: error?.message,
      });
    }

    this.stopLoading();
  }

  private async returnToNewMessage(contact: InboxContact): Promise<void> {
    contact.lastName = contact.lastName || '';

    this.router.navigate(['../new-message', contact], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve',
      state: {
        contact: contact,
      },
    });
  }

  private startLoading(): void {
    this.isLoading = true;
  }

  private stopLoading(): void {
    this.isLoading = false;
  }

  close() {
    this.location.back();
  }
}
